import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { useRolloutChecklist } from '../../hooks/useRollout';
import { CLAUDE_FOLLOW_PROMPT, CLAUDE_FOLLOW_STEPS } from '../../components/Rollout/claudeFollow';
import { ChecklistDeploy, ChecklistStep, tickChecklistStep } from '../../api/rollout';
import { useCockpit } from '../data/CockpitContext';
import { ChecklistOverviewWithChoice, chooseNextDeploy, measureDeploy } from '../data/rollout';
import { dayLabel, when } from '../data/format';
import { apiError, track } from '../data/usePoll';
import { Card, CheckTone, Empty, ErrorNotice, Mark, Spinner } from '../components/ui';
import { Facts, splitTop } from '../components/Facts';
import { v2 } from '../paths';
import { copyText } from '../data/clipboard';

const PHASE_LABEL: Record<ChecklistStep['phase'], string> = { before: 'Vooraf', deploy: 'Deploy', after: 'Erna', followup: 'Opvolgen' };
const STATUS_LABEL: Record<ChecklistDeploy['status'], string> = { open: 'Gepland', wacht: 'Wacht', bezig: 'Bezig', klaar: 'Klaar' };
const STATUS_CHIP: Record<ChecklistDeploy['status'], string> = { open: 'chip', wacht: 'chip warn', bezig: 'chip info', klaar: 'chip ok' };

/** Who does a step: the dashboard by measuring, Claude through the c2e-ops MCP, or you. */
const KIND: Record<ChecklistStep['kind'], { label: string; cls: string; what: string }> = {
    Auto: { label: 'Automatisch', cls: 'kind auto', what: 'Het dashboard meet dit zelf, live. Groen is in orde; oranje of rood zegt wat er niet klopt.' },
    Mcp: { label: 'Claude', cls: 'kind claude', what: 'Kopieer de opdracht naar Claude Code. Claude doet de stap met de c2e-ops MCP en vinkt hem af met het bewijs.' },
    Manual: { label: 'Jij', cls: 'kind you', what: 'Doe je zelf, in Azure DevOps of bij de klant, en vink je hier af.' },
};

const stepTone = (s: ChecklistStep): CheckTone =>
    s.state === 'Done' ? 'ok' : s.state === 'Bad' ? 'bad' : s.state === 'Warn' ? 'warn' : s.state === 'Waiting' ? 'wait' : 'todo';

const segment = (s: ChecklistStep, isNext: boolean) =>
    s.state === 'Done' ? 'done' : s.state === 'Bad' || s.state === 'Warn' ? 'block' : isNext || s.state === 'Waiting' ? 'run' : '';

/** "lumio/prod" or "lumio prod" as a customer and environment, to link to its monitor. */
const splitEnv = (value: string) => {
    const [customer, environment] = value.split(/[/ ]/);
    return { customer, environment };
};

/** "Pipelines › x › Run pipeline: What to deploy a · Branch b" as a path of crumbs and the values to fill in. */
const HowYou = ({ how }: { how: string }) => {
    const colon = how.indexOf(': ');
    const path = (colon > 0 ? how.slice(0, colon) : how).split(' › ');
    const values = colon > 0 ? splitTop(how.slice(colon + 2), '·') : [];
    if (path.length < 2) return <div className="mut" style={{ fontSize: 13 }}>{how}</div>;
    return (
        <div className="stack" style={{ gap: 6 }}>
            <span className="hrow" style={{ gap: 4 }}>
                {path.map((p, i) => (
                    <React.Fragment key={i}>
                        {i > 0 && <span className="mut">›</span>}
                        <span className="fact-chip">{p}</span>
                    </React.Fragment>
                ))}
            </span>
            {values.length > 0 && (
                <span className="hrow" style={{ gap: 5 }}>
                    {values.map((v, i) => (
                        <span key={i} className="fact-chip mono">{v}</span>
                    ))}
                </span>
            )}
        </div>
    );
};

/** The one line to give Claude for a step, with a copy button. */
const AskClaude = ({ prompt }: { prompt: string }) => {
    const [copied, setCopied] = useState(false);
    return (
        <div className="ask">
            <code>{prompt}</code>
            <button
                className="btn sm"
                onClick={() => {
                    setCopied(copyText(prompt));
                    setTimeout(() => setCopied(false), 1500);
                }}
            >
                {copied ? 'Gekopieerd' : 'Kopieer voor Claude'}
            </button>
        </div>
    );
};

const Step = ({ deploy, step, busy, onTick }: { deploy: ChecklistDeploy; step: ChecklistStep; busy: boolean; onTick: () => void }) => {
    const open = step.state !== 'Done';
    const kind = KIND[step.kind];
    return (
        <div className="st">
            <Mark tone={stepTone(step)} />
            <div style={{ minWidth: 0 }}>
                <div className="lbl">{step.title}</div>
                {step.detail && (step.kind === 'Auto' ? <Facts text={step.detail} /> : <div className="mut" style={{ fontSize: 13, marginTop: 2 }}>{step.detail}</div>)}
                {step.tick && (
                    <div className="mut" style={{ fontSize: 12, marginTop: 6 }}>
                        ✓ {step.tick.by ?? 'iemand'} · {when(step.tick.at)}
                        {step.tick.note && (
                            <details style={{ marginTop: 4 }}>
                                <summary style={{ cursor: 'pointer' }}>Bewijs</summary>
                                <div style={{ marginTop: 4, color: '#C3CBD6' }}>{step.tick.note}</div>
                            </details>
                        )}
                    </div>
                )}
                {open && step.kind === 'Mcp' && (
                    <div style={{ marginTop: 8 }}>
                        <AskClaude prompt={`/c2e-rollout ${deploy.title}: ${step.title}`} />
                        {step.how && (
                            <details style={{ marginTop: 6 }}>
                                <summary className="mut" style={{ cursor: 'pointer', fontSize: 12 }}>Wat Claude daarvoor doet</summary>
                                <div className="mut" style={{ fontSize: 12, marginTop: 4 }}>{step.how}</div>
                            </details>
                        )}
                    </div>
                )}
                {open && step.kind === 'Manual' && step.how && (
                    <div style={{ marginTop: 8 }}>
                        <HowYou how={step.how} />
                    </div>
                )}
                {open && step.kind === 'Auto' && step.how && (
                    <details style={{ marginTop: 6 }}>
                        <summary className="mut" style={{ cursor: 'pointer', fontSize: 12 }}>Hoe het gemeten wordt</summary>
                        <div className="mut" style={{ fontSize: 12, marginTop: 4 }}>{step.how}</div>
                    </details>
                )}
            </div>
            <div className="hrow" style={{ gap: 6, justifyContent: 'flex-end', alignItems: 'flex-start' }}>
                <span className={kind.cls} title={kind.what}>{kind.label}</span>
                {step.kind === 'Manual' && (
                    <button className="btn sm" disabled={busy} onClick={onTick}>
                        {busy ? <Spinner /> : step.state === 'Done' ? 'Terugdraaien' : 'Afvinken'}
                    </button>
                )}
            </div>
        </div>
    );
};

export const DeployPage = () => {
    const { deployId } = useParams();
    const axios = useAxiosSwagger();
    const navigate = useNavigate();
    const { find } = useCockpit();
    const { overview: raw, live, error, apply, reload } = useRolloutChecklist(deployId);
    const overview = raw as ChecklistOverviewWithChoice | undefined;
    const [busy, setBusy] = useState<string>();
    const [actionError, setActionError] = useState<string>();

    const deploys = overview?.deploys ?? [];
    const next = deploys.find((d) => d.id === overview?.next);
    const selected = deploys.find((d) => d.id === deployId) ?? next ?? deploys[0];
    const done = deploys.filter((d) => d.status === 'klaar').length;
    const allSteps = deploys.reduce((n, d) => n + d.total, 0);
    const allDone = deploys.reduce((n, d) => n + d.done, 0);

    const run = async (key: string, work: () => Promise<void>) => {
        setBusy(key);
        setActionError(undefined);
        try {
            await track(work());
        } catch (e) {
            setActionError(apiError(e));
        } finally {
            setBusy(undefined);
        }
    };

    const tick = (d: ChecklistDeploy, s: ChecklistStep) => axios && run(s.key, async () => apply(await tickChecklistStep(axios, d.id, s.key, s.state !== 'Done')));
    const choose = (id: string | null) => axios && run(`next:${id}`, async () => {
        await chooseNextDeploy(axios, id);
        reload();
    });
    const measure = (d: ChecklistDeploy) => axios && run(`measure:${d.id}`, async () => apply(await measureDeploy(axios, d.id)));

    return (
        <>
            <div className="pagehead">
                <div>
                    <div className="eyebrow">{overview?.branch ?? 'Afvinklijst'}</div>
                    <h1 className="h1">Deploy {overview?.title ? `· ${overview.title}` : ''}</h1>
                </div>
                <div className="hrow">
                    <div style={{ width: 260 }}>
                        <div className="hrow mono" style={{ justifyContent: 'space-between', fontSize: 12 }}>
                            <span>{done} van {deploys.length} deploys klaar</span>
                            <span className="mut">{allSteps ? Math.round((allDone / allSteps) * 100) : 0}%</span>
                        </div>
                        <div className="meter" style={{ height: 8, marginTop: 6 }}>
                            <b className="i" style={{ width: `${allSteps ? (allDone / allSteps) * 100 : 0}%` }} />
                        </div>
                    </div>
                    <span className={live ? 'live' : 'live stale'}>
                        <span className="dot" />
                        {live ? 'Live' : 'Verbinden…'}
                    </span>
                </div>
            </div>

            <ErrorNotice error={error ?? actionError} />

            {/* Folded: the chips on each step say who does it, and the deploy itself goes first on the page. */}
            <details className="card">
                <summary className="ch" style={{ cursor: 'pointer', borderBottom: 0 }}>
                    <span className="ct">Wie doet wat · automatisch met Claude</span>
                </summary>
                <div className="cb" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div className="kinds">
                        {(['Auto', 'Mcp', 'Manual'] as const).map((k) => (
                            <div key={k} className="kindline">
                                <span className={KIND[k].cls}>{KIND[k].label}</span>
                                <span className="mut">{KIND[k].what}</span>
                            </div>
                        ))}
                    </div>
                    <ol style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                        {CLAUDE_FOLLOW_STEPS.map((s) => (
                            <li key={s}>{s}</li>
                        ))}
                    </ol>
                    <AskClaude prompt={CLAUDE_FOLLOW_PROMPT} />
                </div>
            </details>

            {next && (
                <section className="next au">
                    <span className="who au">{next.status === 'bezig' ? 'NU' : 'VOLG.'}</span>
                    <div className="hrow" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                            <div className="hrow" style={{ gap: 8 }}>
                                <span style={{ fontSize: 18, fontWeight: 600 }}>{next.title}</span>
                                {overview?.chosen && <span className="chip info">door jou gekozen</span>}
                            </div>
                            <div className="mut" style={{ marginTop: 4 }}>{next.next ?? 'bezig'}</div>
                            <div className="prog" style={{ width: 320 }}>
                                <b style={{ width: `${(next.done / Math.max(1, next.total)) * 100}%` }} />
                            </div>
                        </div>
                        <div className="hrow">
                            {overview?.chosen && (
                                <button className="btn ghost" disabled={!!busy} onClick={() => choose(null)}>Terug naar planvolgorde</button>
                            )}
                            {next.environments.map((e) => {
                                const { customer, environment } = splitEnv(e);
                                return customer && environment && find(customer, environment) ? (
                                    <Link key={e} className="btn" to={v2.monitor(customer, environment)}>
                                        Monitor {customer} {environment}
                                    </Link>
                                ) : null;
                            })}
                        </div>
                    </div>
                </section>
            )}

            <div className="g2" style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.2fr)' }}>
                <Card title="Per deploy" right={<span className="mut" style={{ fontSize: 12 }}>kies welke als volgende gaat</span>}>
                    {!overview && !error && <Empty>Laden…</Empty>}
                    {deploys.map((d) => {
                        const nextIndex = d.steps.findIndex((s) => s.state !== 'Done');
                        const isNext = d.id === next?.id;
                        return (
                            <div key={d.id} className={`li ${selected?.id === d.id ? 'on' : ''}`} style={{ gridTemplateColumns: 'minmax(0,1fr) auto', gap: 12, cursor: 'pointer' }} onClick={() => navigate(v2.deploy(d.id))}>
                                <span style={{ minWidth: 0 }}>
                                    <span className="hrow" style={{ gap: 8 }}>
                                        <span className="t">{d.title}</span>
                                        <span className={STATUS_CHIP[d.status]}>{STATUS_LABEL[d.status]}</span>
                                        {isNext && <span className="chip info">volgende</span>}
                                    </span>
                                    <span className="m">
                                        {d.environments.slice(0, 3).map((e) => (
                                            <span key={e} className={`env ${e.toLowerCase().includes('prod') ? 'prod' : 'test'}`}>{e}</span>
                                        ))}
                                        {d.notBefore && <span>niet vóór {dayLabel(d.notBefore).toLowerCase()}</span>}
                                    </span>
                                    <span className="segs" style={{ marginTop: 8 }}>
                                        {d.steps.map((s, i) => (
                                            <i key={s.key} className={segment(s, i === nextIndex && d.status === 'bezig')} title={s.title} />
                                        ))}
                                    </span>
                                </span>
                                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                                    <span className="mono mut" style={{ fontSize: 11 }}>{d.done} / {d.total}</span>
                                    {!isNext && d.status !== 'klaar' && (
                                        <button
                                            className="btn sm ghost"
                                            disabled={!!busy}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                choose(d.id);
                                            }}
                                        >
                                            {busy === `next:${d.id}` ? <Spinner /> : 'Als volgende'}
                                        </button>
                                    )}
                                </span>
                            </div>
                        );
                    })}
                </Card>

                {selected && (
                    <Card
                        title={
                            <div>
                                <div className="h2">{selected.title}</div>
                                {selected.why && <div className="mut" style={{ fontSize: 12, marginTop: 2 }}>{selected.why}</div>}
                            </div>
                        }
                        right={
                            <span className="hrow" style={{ gap: 8 }}>
                                {selected.checkedAt && <span className="mut" style={{ fontSize: 12 }}>gemeten {when(selected.checkedAt)}</span>}
                                <button className="btn sm" disabled={!!busy} onClick={() => measure(selected)}>
                                    {busy === `measure:${selected.id}` ? <Spinner /> : 'Nu meten'}
                                </button>
                            </span>
                        }
                    >
                        {(['before', 'deploy', 'after', 'followup'] as const).map((phase) => {
                            const steps = selected.steps.filter((s) => s.phase === phase);
                            if (steps.length === 0) return null;
                            return (
                                <React.Fragment key={phase}>
                                    <div className="navh" style={{ paddingLeft: 16 }}>{PHASE_LABEL[phase]}</div>
                                    <div className="steps">
                                        {steps.map((s) => (
                                            <Step key={s.key} deploy={selected} step={s} busy={busy === s.key} onTick={() => tick(selected, s)} />
                                        ))}
                                    </div>
                                </React.Fragment>
                            );
                        })}
                    </Card>
                )}
            </div>
        </>
    );
};
